/**
 * @typedef { import('vue/types').VNode } VNode
 * @typedef { import('vue/types').CreateElement } CreateElement
 */

import {h, type VNode} from 'vue';
import {isNone} from '../js_utils.ts';
import {isUrl} from '../utils/url.ts';
import {SvgIcon} from '../svg.ts';
import {UNKNOWN_LAYER} from '../allspice_app/stores/layers.ts';

class InspectorContext {
  appSubUrl: string;
  repoOwner: string;
  repoName: string;
  commitSHA: string;
  layers: Record<number, string>;
  findHyperlinks: boolean;
  parseAsExternalDesignBlock: boolean;
  externalDesignBlocks: Record<string, string>;
  projectDocuments: Record<string, unknown>;
  treePath: string;
  projectPath: string | null;
  variants: {[key: string]: string};
  repoUrl: string;
  options: {
    replaceLayerIdsWithNames: boolean;
  };

  constructor({window, treePath, repoOwner, repoName, commitSHA, layers, externalDesignBlocks, projectDocuments, projectPath, variants, options}: {
    window: Window,
    treePath: string,
    repoOwner: string,
    repoName: string,
    commitSHA: string,
    layers: Record<number, {id: number, name: string}>,
    externalDesignBlocks: Record<string, string>,
    projectDocuments: Record<string, unknown>,
    projectPath: string | null,
    variants: {[key: string]: string},
    options?: Partial<{
      replaceLayerIdsWithNames: boolean;
    }>,
  }) {
    if (window !== null) {
      const {appSubUrl} = window.config;
      this.appSubUrl = appSubUrl;
    } else {
      this.appSubUrl = '';
    }
    this.repoOwner = repoOwner;
    this.repoName = repoName;
    this.commitSHA = commitSHA;

    this.layers = {};
    for (const layer of Object.values(layers)) {
      this.layers[layer.id] = layer.name;
    }

    this.findHyperlinks = false;
    this.parseAsExternalDesignBlock = false;
    this.externalDesignBlocks = externalDesignBlocks;
    this.projectDocuments = projectDocuments;
    this.treePath = treePath;
    this.projectPath = projectPath;
    this.variants = variants;
    if (window !== null) {
      this.repoUrl = `${window.config.appUrl + this.appSubUrl + this.repoOwner}/${this.repoName}`;
    } else {
      this.repoUrl = `/${this.appSubUrl + this.repoOwner}/${this.repoName}`;
    }

    options = options ?? {};
    this.options = {
      replaceLayerIdsWithNames: false,
      ...options,
    };
  }

  /**
   * Get the url for the given repo file
   * @param {string} filename
   * @param {Object} opts
   * @returns {string}
   */
  formatFileUrl(filename: string, {absolutePath = false} = {}) {
    const urlPrefix = `${this.appSubUrl}/${this.repoOwner}/${this.repoName}/src/commit/${this.commitSHA}`;
    try {
      let base = `${window.location.origin}${urlPrefix}/${this.treePath}`;
      if (absolutePath) {
        base = `${window.location.origin}${urlPrefix}/`;
      }
      let ret = new URL(filename, base);

      // validate that the url doesn't navigate outside of the repo
      if (!ret.pathname.startsWith(urlPrefix)) {
        // fallback on trying to format the filename as a url directly
        ret = new URL(filename.split('/').pop(), base);
      }

      return ret.pathname;
    } catch (e) {
      console.error(e);
      return `${urlPrefix}/${encodeURIComponent(filename)}`;
    }
  }
}

class ObjectValue {
  contents: Record<string, unknown> | null;

  constructor(contents: Record<string, unknown> | null) {
    this.contents = contents;
  }
  /**
   * Whether this value is null
   */
  isNull(): boolean {
    return false;
  }

  /**
   * Check if this object contains the following key
   */
  getObjectValue(key: string): unknown {
    return this.contents !== null && key in this.contents ? this.contents[key] : null;
  }

  /**
   * Return the raw value from the tree
   */
  rawValue(): Record<string, unknown> | string | null {
    return this.contents;
  }

  /**
   * Return the value
   */
  value(props: Record<string, unknown>): VNode {
    return h('div', props, '');
  }

  /**
   * Set the units for this value
   */
  withUnits(_units: string): ObjectValue {
    return this;
  }

  /**
   * Objects are not primitives
   */
  isPrimitive(): boolean {
    return false;
  }
}

class Value extends ObjectValue {
  val: string;
  units: string;
  linkRef: string;
  linkNewTab: boolean;
  icon: string;

  constructor(val: string) {
    super({val});
    this.val = val;
    this.val = val;
    this.units = '';
    this.linkRef = '';
    this.linkNewTab = false;
    this.icon = '';
  }

  /**
   * Whether this value is null
   */
  isNull(): boolean {
    return false;
  }

  /**
   * Return the raw value from the tree
   */
  rawValue(): string {
    return this.val + this.units;
  }

  /**
   * Primitives are not objects
   */
  getObjectValue(_key: string): unknown {
    return null;
  }

  /**
   * Return the value
   * @param {object} props
   * @returns {VNode}
   */
  value(props: Record<string, unknown>) {
    if (this.linkRef !== '') {
      const linkProps: Record<string, string> = {href: this.linkRef};
      if (this.linkNewTab) {
        linkProps.target = '_blank';
        linkProps.rel = 'noopener noreferrer';
      }
      const children = [
        h('a', linkProps, `${this.val + this.units}`),
      ];
      if (this.icon !== '') {
        children.unshift(
          h(SvgIcon, {
            name: this.icon,
          }),
        );
      }
      props['class'] = 'tw:flex tw:items-center tw:gap-2';
      return h('div', props, children);
    }
    return h('div', props, this.val + this.units);
  }

  /**
   * Set the units for this value
   */
  withUnits(units: string): ObjectValue {
    this.units = units;
    return this;
  }

  /**
   * Render this value as a hyperlink
   */
  withLink({url, linkNewTab, icon}: {
    url: string,
    linkNewTab: boolean,
    icon: string,
  }): Value {
    this.linkRef = url;
    this.linkNewTab = linkNewTab;
    this.icon = icon;
    return this;
  }

  /**
   * Values are primitives
   */
  isPrimitive(): boolean {
    return true;
  }
}

class NullValue extends ObjectValue {
  constructor() {
    super(null);
  }
  /**
   * Whether this value is null
   * @returns {boolean}
   */
  isNull() {
    return true;
  }

  /**
   * Return the raw value from the tree
   */
  rawValue(): string {
    return 'null';
  }

  /**
   * Null values are not objects
   */
  getObjectValue(_key: string): unknown {
    return null;
  }

  /**
   * Return the value
   */
  value(props: Record<string, unknown>): VNode {
    return h('div', props, 'null');
  }

  /**
   * Null values are primitives
   */
  isPrimitive(): boolean {
    return true;
  }
}

class KeyValue {
  key: string;
  value: ObjectValue;

  constructor(key: string, value: ObjectValue) {
    this.key = key;
    this.value = value;
  }
}

const UNIT_KEYS = new Set([
  // Text
  'points',
  'text_height',
  'text_width',
  // Track
  'linewidth',
  'line_width',
  // Pad
  'offset',
  'offset_from_hole',
  'top_size',
  'hole_size',
  // Via
  'diameter',
  // Arc
  'radius',
  // Region
  'vertices',
  // PCB Component
  'position',
]);

const NUMBER_FORMAT = new Intl.NumberFormat(undefined, {style: 'decimal', maximumFractionDigits: 5});

/**
 * For a given key, determine whether to add units to child/children
 * @param {string} key
 * @returns boolean
 */
function addUnitsForKey(key: string): boolean {
  return UNIT_KEYS.has(key);
}

/**
 * Check if a value is a number that should be formatted
 * @param {object} val
 * @returns {boolean}
 */
function needsNumberFormat(val: unknown): val is number {
  return typeof val === 'number' &&
  !Number.isNaN(val);
}

/**
 * Transform this key value pair in some way
 * before adding it to the tree
 * @param {string} key
 * @param {object | null | undefined} val
 * @param {InspectorContext} metadataCtx
 * @returns {KeyValue}
 */
function processKeyValue(key: string, val: unknown, metadataCtx: InspectorContext): KeyValue {
  if (val === null || val === undefined) {
    return new KeyValue(key, new NullValue());
  }

  // perform any transformations
  if (key === 'filename') {
    const strVal = String(val);
    if (metadataCtx.parseAsExternalDesignBlock && strVal in metadataCtx.externalDesignBlocks) {
      const url = metadataCtx.externalDesignBlocks[strVal];
      let icon = 'octicon-file-submodule';

      // If the link is in the repo, remove the external icon
      if (url.startsWith(metadataCtx.repoUrl)) {
        icon = '';
      }

      return new KeyValue('filename', new Value(strVal).withLink({
        url,
        linkNewTab: false,
        icon,
      }));
    }

    // as a fallback we'll just format the url directly
    let url = metadataCtx.formatFileUrl(strVal);

    // both this value and the project documents will be absolute relative to the repo.
    // Add a leading / to ensure that we don't overmatch
    const absolutePath = `/${strVal}`;

    // check if this filename is referenced in the project documents
    for (const docPath of Object.values(metadataCtx.projectDocuments)) {
      if (typeof docPath === 'string' && (`/${docPath}`).endsWith(absolutePath)) {
        url = metadataCtx.formatFileUrl(docPath, {absolutePath: true});
        break;
      }
    }

    return new KeyValue('filename', new Value(strVal).withLink({url, linkNewTab: false, icon: ''}));
  } else if (key === 'layer_id' || key === 'start_layer_id' || key === 'end_layer_id') {
    if (metadataCtx.options.replaceLayerIdsWithNames) {
      const layerName = metadataCtx.layers[Number(val)] ?? UNKNOWN_LAYER.name;
      return new KeyValue(key, new Value(layerName));
    }

    const layerId = Object.keys(metadataCtx.layers).includes(val.toString()) ? Number(val) : UNKNOWN_LAYER.id;
    return new KeyValue(key, new Value(layerId.toString()));
  } else if (key === 'variants') {
    // Handle variant objects - transform variant IDs to variant names
    const transformedVariants: Record<string, unknown> = {};
    for (const [variantKey, variantValue] of Object.entries(val as Record<string, unknown>)) {
      const variantName = metadataCtx.variants[variantKey] || variantKey;
      if (variantValue === null) {
        transformedVariants[variantName] = 'Not Populated';
      } else {
        transformedVariants[variantName] = variantValue;
      }
    }
    return new KeyValue(key, new ObjectValue(transformedVariants));
  } else if (key === 'variant_text') {
    // Handle variant_text objects - transform variant IDs to variant names
    const transformedVariantText: Record<string, unknown> = {};
    for (const [variantKey, variantText] of Object.entries(val as Record<string, unknown>)) {
      const variantName = metadataCtx.variants[variantKey] || variantKey;
      transformedVariantText[variantName] = variantText;
    }
    return new KeyValue(key, new ObjectValue(transformedVariantText));
  } else if (key === 'attributes') {
    // Handle attributes objects - transform hash_id keys to attribute names
    const attrEntries = Object.entries(val as Record<string, {name?: string}>).map(([attribute_id, attribute]): [string, {name?: string}] => {
      const displayKey = attribute.name || attribute_id;
      return [displayKey, attribute];
    });
    attrEntries.sort((a, b) => {
      const displayKeyA = a[0];
      const displayKeyB = b[0];
      return displayKeyA.localeCompare(displayKeyB, undefined, {sensitivity: 'base'});
    });
    const transformedAttributes: Record<string, unknown> = {};
    for (const [displayKey, attrData] of attrEntries) {
      transformedAttributes[displayKey] = attrData;
    }
    return new KeyValue(key, new ObjectValue(transformedAttributes));
  } else if (key === 'pins') {
    // Handle pins objects - transform pin hashes to readable names with designator and name
    // Sort by designator
    const pinEntries = Object.entries(val as Record<string, {designator?: string; name?: string}>);
    pinEntries.sort((a, b) => {
      const designatorA = a[1]?.designator ?? '';
      const designatorB = b[1]?.designator ?? '';
      return designatorA.localeCompare(designatorB, undefined, {numeric: true, sensitivity: 'base'});
    });
    const transformedPins: Record<string, {designator?: string; name?: string}> = {};
    for (const [_pinHash, pinData] of pinEntries) {
      const designator = pinData?.designator ?? '';
      const name = pinData?.name ?? '';
      let displayKey: string;
      if (designator) {
        displayKey = name ? `(${designator}) ${name}` : `(${designator})`;
      } else {
        displayKey = name || '(unnamed)';
      }
      transformedPins[displayKey] = pinData;
    }
    return new KeyValue(key, new ObjectValue(transformedPins));
  }

  // by default, we'll format as an object block
  if (typeof val === 'object') {
    return new KeyValue(key, new ObjectValue(val as Record<string, unknown>));
  }

  if (needsNumberFormat(val)) {
    return new KeyValue(key, new Value(
      NUMBER_FORMAT.format(val),
    ));
  }

  if (metadataCtx.findHyperlinks && isUrl(String(val))) {
    // Don't fetch the url but render as a hyperlink
    return new KeyValue(key, new Value(String(val)).withLink({url: String(val), linkNewTab: true, icon: ''}));
  }

  return new KeyValue(key, new Value(String(val)));
}

export class TreeNode {
  key: string;
  head: ObjectValue;
  base: ObjectValue;
  primitive: boolean;
  children: TreeNode[];
  empty: boolean;
  level: number;
  id: string;
  collapsed: boolean;
  added: boolean;
  modified: boolean;
  removed: boolean;

  /**
   * @param {string} key
   * @param {ObjectValue} head
   * @param {ObjectValue} base
   * @param {boolean} primitive
   * @param {boolean} empty
   * @param {number} level
   * @param {string} id
   * @param {boolean} collapsed
   * @param {boolean} added
   * @param {boolean} modified
   * @param {boolean} removed
   */
  constructor(key: string, head: ObjectValue, base: ObjectValue, primitive: boolean, empty: boolean, level: number, id: string, collapsed: boolean, added: boolean, modified: boolean, removed: boolean) {
    this.key = key;
    this.head = head;
    this.base = base;
    this.primitive = primitive;
    this.children = [];
    this.empty = empty;
    this.level = level;
    this.id = id;
    this.collapsed = collapsed;
    this.added = added;
    this.modified = modified;
    this.removed = removed;
  }

  /**
   * Add a child to this tree node
   * @param {TreeNode} child
   */
  addChild(child: TreeNode) {
    this.children.push(child);
    this.modified = this.modified || child.modified || child.added || child.removed;
    this.empty = false;
    this.collapsed = this.level > 0;
  }

  /**
 * Get a child treenode with the given key or null
 */
  childNode(key: string): TreeNode | null {
    for (const child of this.children) {
      if (child.key === key) {
        return child;
      }
    }
    return null;
  }
}

function readableChildValue(node: TreeNode | null): string | null {
  if (node === null) {
    return null;
  }

  const headValue = node.head.rawValue();
  if (typeof headValue === 'string' && headValue !== '' && headValue !== 'null') {
    return headValue;
  }

  const baseValue = node.base.rawValue();
  if (typeof baseValue === 'string' && baseValue !== '' && baseValue !== 'null') {
    return baseValue;
  }

  return null;
}

function looksLikeOpaqueKey(key: string): boolean {
  const normalizedKey = key.replace(/[-_]/g, '');
  return normalizedKey.length >= 12 && /^[0-9a-f]+$/i.test(normalizedKey);
}

function matchesIdentityField(node: TreeNode, key: string): boolean {
  for (const identityField of ['id', 'unique_id', 'hash_id']) {
    if (readableChildValue(node.childNode(identityField)) === key) {
      return true;
    }
  }

  return false;
}

export function getTreeNodeDisplayLabel(key: string, node: TreeNode | null): string {
  if (key === '' || node === null) {
    return key;
  }

  const name = readableChildValue(node.childNode('name'));
  if (name === null) {
    return key;
  }

  if (matchesIdentityField(node, key) || looksLikeOpaqueKey(key)) {
    return name;
  }

  return key;
}

/**
 * Get the union of keys in two objects
 */
function unionKeys(first: ObjectValue, second: ObjectValue): string[] {
  if ((first.isNull()) || first.isPrimitive()) {
    return Object.keys(second.rawValue());
  }
  if ((second.isNull()) || second.isPrimitive()) {
    return Object.keys(first.rawValue());
  }
  return Array.from(new Set([...Object.keys(first.rawValue()), ...Object.keys(second.rawValue())]));
}

/**
 * Build a tree structure from json to render
 */
type Contents = unknown;
function buildTree(
  key: string, headContents: Contents, baseContents: Contents, level: number,
  parentAdded: boolean, parentRemoved: boolean, isDiff: boolean,
  units: string, addUnits: boolean,
  parent: TreeNode | null,
  metadataCtx: InspectorContext,
): TreeNode {
  let added = parentAdded;
  let removed = parentRemoved;
  const origKey = key;

  const headKV = processKeyValue(origKey, headContents, metadataCtx);
  const baseKV = processKeyValue(origKey, baseContents, metadataCtx);
  const processedKey = headKV.key !== '' ? headKV.key : baseKV.key;
  let headValue = headKV.value;
  let baseValue = baseKV.value;

  const diffingVariants = isDiff && parent !== null && parent.key === 'variants';
  const exactlyOneValueIsPrimitive = (
    (headValue.isPrimitive() || baseValue.isPrimitive()) &&
    !(headValue.isPrimitive() && baseValue.isPrimitive())
  );

  if (diffingVariants && exactlyOneValueIsPrimitive) {
    if (headValue.isPrimitive()) {
      headValue = new ObjectValue({'component': 'Not populated'});
    } else {
      baseValue = new ObjectValue({'component': 'Not populated'});
    }
  }

  let primitive = false;

  if (baseValue.isNull()) {
    if (headValue.isNull()) {
      // null value
      return new TreeNode(
        processedKey,
        headValue,
        baseValue,
        true,
        false,
        level,
        '',
        false,
        false,
        false,
        false,
      );
    }

    added = true;
    primitive = headValue.isPrimitive();
  } else {
    primitive = baseValue.isPrimitive();

    if (headValue.isNull()) {
      removed = true;
    } else {
      // if head is a primitive even though base is an object
      primitive = headValue.isPrimitive();
    }
  }

  if (primitive) {
    return new TreeNode(
      processedKey,
      headValue.withUnits(addUnits ? units : ''),
      baseValue.withUnits(addUnits ? units : ''),
      true,
      false,
      level,
      '',
      false,
      added && isDiff,
      (headValue.rawValue() !== baseValue.rawValue()) && !added && !removed && isDiff,
      removed && isDiff,
    );
  }

  // take the union of keys in head, base
  const allKeys = unionKeys(headValue, baseValue);

  // we want to resolve hyperlinks that are children of attributes
  const metadataHyperlinkState = metadataCtx.findHyperlinks;
  metadataCtx.findHyperlinks = metadataCtx.findHyperlinks || key === 'attributes';

  // if this sheetref is an external design block, we'll need to use project json to resolve the link
  metadataCtx.parseAsExternalDesignBlock = metadataCtx.parseAsExternalDesignBlock ||
    (level === 0 && headValue.getObjectValue('external_sheet') !== null);

  const treeNode = new TreeNode(
    processedKey,
    new ObjectValue(null),
    new ObjectValue(null),
    false,
    true,
    level,
    '',
    false,
    added && isDiff,
    false, // updated once children added
    removed && isDiff,
  );

  for (const key of allKeys) {
    treeNode.addChild(buildTree(
      key,
      headValue.getObjectValue(key),
      baseValue.getObjectValue(key),
      level + 1,
      added,
      removed,
      isDiff,
      units,
      addUnits || addUnitsForKey(key),
      treeNode,
      metadataCtx,
    ));
  }

  // reset the state
  metadataCtx.findHyperlinks = metadataHyperlinkState;

  return treeNode;
}

/**
 * Extract a value by key out of the head or base object.  Prefers the head.
 * @param {object | null | undefined} headObj
 * @param {object | null | undefined} baseObj
 * @param {string} key
 * @param {object | null} defaultVal
 * @return {object | null}
 */
function headOrBase(headObj: Record<string, unknown> | unknown[] | null | undefined, baseObj: Record<string, unknown> | unknown[] | null | undefined, key: string, defaultVal: unknown): unknown {
  if (!isNone(headObj)) {
    if (Array.isArray(headObj)) {
      const value = findByIdLinearScan(headObj as Record<string, unknown>[], key);
      if (value !== undefined) return value;
    } else if (key in headObj) {
      return headObj[key];
    }
  }
  if (!isNone(baseObj)) {
    if (Array.isArray(baseObj)) {
      const value = findByIdLinearScan(baseObj as Record<string, unknown>[], key);
      if (value !== undefined) return value;
    } else if (key in baseObj) {
      return baseObj[key];
    }
  }
  return defaultVal;
}

/**
 * Find a value from an array of objects whose `id` property matches the given.
 * If it's not found, `undefined` is returned.  A found element can't be
 * `undefined` since it must have had an `id` property.
 * @param {Array<object>} arr
 * @param {string} target
 * @return {object | undefined}
 */
function findByIdLinearScan(arr: Record<string, unknown>[], target: string): Record<string, unknown> | undefined {
  return arr.find((element: Record<string, unknown>) => {
    /** @type string | number | object | null | undefined */
    const id = element?.id;
    if (isNone(id)) return false;
    return id === target || id.toString() === target;
  });
}

export {buildTree, processKeyValue, Value, NullValue, InspectorContext, headOrBase, ObjectValue};
